import React from "react";

export interface ListColumn<T> {
  key: keyof T | string;
  label: React.ReactNode;
  render?: (item: T) => React.ReactNode;
  className?: string;
  sortable?: boolean;
  width?: string; // e.g., "2fr", "120px", "20%"
}

export interface ListAction<T> {
  label: string;
  icon: React.ReactNode;
  onClick: (item: T) => void;
  disabled?: (item: T) => boolean;
  className?: string;
  title?: string;
}

export interface DataListProps<T> {
  data: T[];
  columns: ListColumn<T>[];
  actions?: ListAction<T>[];
  keyExtractor: (item: T) => string | number;
  containerClassName?: string;
  headerClassName?: string;
  rowClassName?: string | ((item: T) => string);
  emptyMessage?: string;
  loading?: boolean;
  onRowClick?: (item: T) => void;
  footer?: React.ReactNode;
  showFooter?: boolean;
  itemLabel?: string;
  /** Optional override for the desktop table min-width (e.g. "72rem"). */
  desktopMinWidth?: string;
}

const ACTION_BTN_PX = 32;
const ACTIONS_PAD_PX = 48; // px-4/sm:px-6 both sides

function columnMinRem(width?: string): number {
  if (!width) return 8;
  if (width.includes("fr")) return Math.max(6, parseFloat(width) * 5.5);
  const value = parseFloat(width);
  if (!Number.isNaN(value) && width.includes("px")) return value / 16;
  if (!Number.isNaN(value) && width.includes("rem")) return value;
  return 8;
}

export function DataList<T>({
  data,
  columns,
  actions,
  keyExtractor,
  containerClassName = "bg-white dark:bg-slate-800 rounded-lg sm:rounded-xl shadow-sm border border-gray-200 dark:border-slate-700 overflow-hidden",
  headerClassName = "bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-slate-900 dark:to-slate-800",
  rowClassName,
  emptyMessage = "Nenhum item encontrado",
  loading = false,
  onRowClick,
  footer,
  showFooter = true,
  itemLabel = "item",
  desktopMinWidth,
}: DataListProps<T>) {
  const actionsCount = actions?.length ?? 0;
  const actionsColPx =
    actionsCount > 0 ? actionsCount * ACTION_BTN_PX + ACTIONS_PAD_PX : 0;

  const totalFr = columns.reduce((sum, col) => {
    if (col.width?.includes("fr")) return sum + parseFloat(col.width);
    return sum + 1;
  }, 0);

  const computedMinWidth = `${Math.max(
    48,
    columns.reduce((sum, col) => sum + columnMinRem(col.width), 0) +
      actionsColPx / 16 +
      2
  )}rem`;

  const tableMinWidth = desktopMinWidth || computedMinWidth;

  const headerCellClass = `${headerClassName} px-4 sm:px-6 py-3 sm:py-4 text-xs sm:text-sm font-semibold text-gray-600 dark:text-slate-300 uppercase tracking-wide whitespace-nowrap border-b border-gray-200 dark:border-slate-700`;

  const bodyCellClass =
    "bg-white dark:bg-slate-800 group-hover:bg-gray-50 dark:group-hover:bg-slate-700/50 px-4 sm:px-6 py-3 sm:py-4 align-middle text-sm text-gray-900 dark:text-slate-100 transition-colors duration-150";

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
        <span className="ml-2 text-gray-600 dark:text-slate-300">
          Carregando...
        </span>
      </div>
    );
  }

  if (data.length === 0) {
    return (
      <div className={containerClassName}>
        <div className="text-center py-12">
          <p className="text-gray-500">{emptyMessage}</p>
        </div>
      </div>
    );
  }

  return (
    <div className={containerClassName}>
      <div className="overflow-x-auto">
        <table
          className="hidden md:table w-full table-fixed border-collapse"
          style={{ minWidth: tableMinWidth }}
        >
          <colgroup>
            {columns.map((column, index) => {
              const fr = column.width?.includes("fr")
                ? parseFloat(column.width)
                : 1;
              return (
                <col
                  key={index}
                  style={{ width: `${(fr / totalFr) * 100}%` }}
                />
              );
            })}
            {actionsCount > 0 ? (
              <col style={{ width: `${actionsColPx}px` }} />
            ) : null}
          </colgroup>

          <thead>
            <tr>
              {columns.map((column, index) => (
                <th
                  key={index}
                  scope="col"
                  className={`${headerCellClass} text-left overflow-hidden text-ellipsis ${
                    column.className || ""
                  }`}
                >
                  {column.label}
                </th>
              ))}
              {actionsCount > 0 ? (
                <th
                  scope="col"
                  className={`${headerCellClass} text-right`}
                  style={{ width: actionsColPx }}
                >
                  Ações
                </th>
              ) : null}
            </tr>
          </thead>

          <tbody>
            {data.map((item) => {
              const extraRowClass =
                typeof rowClassName === "function"
                  ? rowClassName(item)
                  : rowClassName || "";

              return (
                <tr
                  key={keyExtractor(item)}
                  className={`group border-b border-gray-100 dark:border-slate-700 last:border-b-0 ${extraRowClass} ${
                    onRowClick ? "cursor-pointer" : ""
                  }`}
                  onClick={() => onRowClick?.(item)}
                >
                  {columns.map((column, columnIndex) => (
                    <td
                      key={columnIndex}
                      className={`${bodyCellClass} max-w-0 overflow-hidden ${
                        column.className || ""
                      }`}
                    >
                      <div className="min-w-0 max-w-full overflow-hidden text-ellipsis whitespace-nowrap">
                        {column.render
                          ? column.render(item)
                          : String(item[column.key as keyof T] || "")}
                      </div>
                    </td>
                  ))}

                  {actionsCount > 0 ? (
                    <td
                      className={`${bodyCellClass} whitespace-nowrap text-right`}
                      style={{ width: actionsColPx }}
                    >
                      <div className="inline-flex items-center justify-end gap-0">
                        {actions!.map((action, actionIndex) => (
                          <button
                            key={actionIndex}
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              action.onClick(item);
                            }}
                            disabled={action.disabled?.(item)}
                            className={`inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed ${
                              action.className ||
                              "text-gray-600 hover:text-gray-800 hover:bg-gray-100 dark:hover:bg-slate-700"
                            }`}
                            title={action.title || action.label}
                          >
                            {action.icon}
                          </button>
                        ))}
                      </div>
                    </td>
                  ) : null}
                </tr>
              );
            })}
          </tbody>
        </table>

        <div className="md:hidden divide-y divide-gray-100 dark:divide-slate-700">
          {data.map((item) => {
            const extraRowClass =
              typeof rowClassName === "function"
                ? rowClassName(item)
                : rowClassName || "";

            return (
              <div
                key={keyExtractor(item)}
                className={`bg-white dark:bg-slate-800 px-4 sm:px-6 py-3 sm:py-4 ${extraRowClass} ${
                  onRowClick ? "cursor-pointer" : ""
                }`}
                onClick={() => onRowClick?.(item)}
              >
                <div className="space-y-3">
                  {columns.map((column, columnIndex) => (
                    <div
                      key={columnIndex}
                      className={`flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 ${
                        column.className || ""
                      }`}
                    >
                      <span className="text-xs font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-wide sm:w-28 flex-shrink-0">
                        {typeof column.label === "string"
                          ? `${column.label}:`
                          : column.label}
                      </span>
                      <div className="flex-1 text-sm sm:text-base text-gray-900 dark:text-slate-100 break-words">
                        {column.render
                          ? column.render(item)
                          : String(item[column.key as keyof T] || "")}
                      </div>
                    </div>
                  ))}

                  {actionsCount > 0 ? (
                    <div className="flex items-center justify-end gap-0 pt-2 border-t border-gray-200 dark:border-slate-700 mt-3">
                      {actions!.map((action, actionIndex) => (
                        <button
                          key={actionIndex}
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            action.onClick(item);
                          }}
                          disabled={action.disabled?.(item)}
                          className={`inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed ${
                            action.className ||
                            "text-gray-600 hover:text-gray-800 hover:bg-gray-100"
                          }`}
                          title={action.title || action.label}
                        >
                          {action.icon}
                        </button>
                      ))}
                    </div>
                  ) : null}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {(footer || showFooter) && (
        <div className="bg-gray-50 dark:bg-slate-900/60 px-4 sm:px-6 py-2 sm:py-3 border-t border-gray-200 dark:border-slate-700">
          {footer || (
            <div className="text-xs sm:text-sm text-gray-500 dark:text-slate-400 text-center">
              {data.length} {itemLabel} encontrado
              {data.length !== 1 ? "s" : ""}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default DataList;
